import axios from "axios";
import { toast } from "sonner";

export const API_URL = process.env.REACT_APP_BACKEND_URL;
export const RICETTE_BASE = `${API_URL}/api/deblistering/ricette`;

export function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function resolveApiBase(base) {
  if (!base) return RICETTE_BASE;
  if (base.startsWith("http")) return base.replace(/\/$/, "");
  return `${API_URL}${base.startsWith("/") ? base : `/${base}`}`.replace(/\/$/, "");
}

export async function apiGet(path, token, base) {
  const res = await axios.get(`${resolveApiBase(base)}${path}`, { headers: authHeaders(token) });
  return res.data;
}

export async function apiPost(path, token, data, base) {
  const res = await axios.post(`${resolveApiBase(base)}${path}`, data, { headers: authHeaders(token) });
  return res.data;
}

export async function apiPatch(path, token, data, base) {
  const res = await axios.patch(`${resolveApiBase(base)}${path}`, data, { headers: authHeaders(token) });
  return res.data;
}

export async function apiPut(path, token, data, base) {
  const res = await axios.put(`${resolveApiBase(base)}${path}`, data, { headers: authHeaders(token) });
  return res.data;
}

export async function apiDelete(path, token, base) {
  const res = await axios.delete(`${resolveApiBase(base)}${path}`, { headers: authHeaders(token) });
  return res.data;
}

export async function apiDownload(path, token, filename = "download") {
  const res = await axios.get(`${RICETTE_BASE}${path}`, {
    headers: authHeaders(token),
    responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function handleApiError(err, fallback = "Errore") {
  toast.error(err.response?.data?.detail || err.message || fallback);
}

export const STATO_RICHIESTA_LABELS = {
  da_gestire: { label: "Da gestire", color: "bg-amber-50 text-amber-700 border border-amber-100" },
  in_attesa_medico: { label: "In attesa medico", color: "bg-blue-50 text-blue-700 border border-blue-100" },
  invio_programmato: { label: "Invio programmato", color: "bg-sky-50 text-sky-700 border border-sky-100" },
  inviata_medico: { label: "Inviata al medico", color: "bg-violet-50 text-violet-700 border border-violet-100" },
  ricette_ricevute: { label: "Ricette ricevute", color: "bg-emerald-50 text-emerald-700 border border-emerald-100" },
  chiusa: { label: "Chiusa", color: "bg-slate-50 text-slate-600 border border-slate-200" },
  scaduta: { label: "Scaduta", color: "bg-red-50 text-red-700 border border-red-100" },
};

export const STATO_RICETTA_LABELS = {
  in_attesa: { label: "In attesa", color: "bg-slate-50 text-slate-600 border border-slate-200" },
  da_completare: { label: "Da completare", color: "bg-amber-50 text-amber-700 border border-amber-100" },
  da_verificare: { label: "Da verificare", color: "bg-violet-50 text-violet-700 border border-violet-100" },
  ricette_ricevute: { label: "Ricette ricevute", color: "bg-blue-50 text-blue-700 border border-blue-100" },
  da_inviare_farmacia: { label: "Da inviare farmacia", color: "bg-cyan-50 text-cyan-700 border border-cyan-100" },
  inviata_farmacia: { label: "Inviata farmacia", color: "bg-indigo-50 text-indigo-700 border border-indigo-100" },
  completata: { label: "Completata", color: "bg-emerald-50 text-emerald-700 border border-emerald-100" },
};

export const STATO_PRATICA_LABELS = {
  attesa_medico: { label: "Attesa Medico", color: "bg-amber-50 text-amber-700 border border-amber-100" },
  pronta_invio: { label: "Pronta Invio", color: "bg-cyan-50 text-cyan-700 border border-cyan-100" },
  completata: { label: "Completata", color: "bg-emerald-50 text-emerald-700 border border-emerald-100" },
  bloccata: { label: "Bloccata", color: "bg-red-50 text-red-700 border border-red-100" },
  archiviata: { label: "Archiviata", color: "bg-slate-50 text-slate-500 border border-slate-200" },
  nessuna_pratica: { label: "Nessuna pratica", color: "bg-slate-50 text-slate-500 border border-slate-200" },
};

export const DESTINAZIONE_OPTIONS = [
  { value: "farmacia", label: "Farmacia" },
  { value: "struttura", label: "Struttura" },
  { value: "non_necessario", label: "Non necessario" },
  { value: "da_verificare", label: "Da verificare" },
];

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/** Indicatori tracking email invio ricette alla farmacia (inviata / consegnata / aperta). */
export function EmailTrackingBadges({ item, compact = false, className = "" }) {
  const inviata = !!(item?.email_inviata_at || item?.stato === "inviata_farmacia" || item?.stato === "completata");
  if (!inviata) {
    return compact ? null : <span className="text-slate-400 text-xs">—</span>;
  }

  const steps = [
    {
      key: "inviata",
      done: true,
      at: item.email_inviata_at,
      label: "Inviata",
      dotClass: "bg-indigo-500",
    },
    {
      key: "consegnata",
      done: !!item.email_consegnata_at,
      at: item.email_consegnata_at,
      label: "Consegnata",
      dotClass: "bg-emerald-500",
    },
    {
      key: "aperta",
      done: !!item.email_aperta_at,
      at: item.email_aperta_at,
      label: "Aperta",
      dotClass: "bg-violet-500",
    },
  ];

  const title = steps
    .map((s) => `${s.label}: ${s.done ? formatDateTime(s.at) : "in attesa"}`)
    .join(" · ");

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-0.5 ${className}`} title={title}>
        {steps.map((s) => (
          <span
            key={s.key}
            className={`w-2 h-2 rounded-full ${s.done ? s.dotClass : "bg-slate-200"}`}
            aria-label={s.label}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-slate-100 bg-slate-50/80 p-3 space-y-2 ${className}`}>
      <div className="text-xs font-semibold text-slate-500">Tracking email farmacia</div>
      {steps.map((s) => (
        <div key={s.key} className="flex items-center justify-between gap-2 text-xs">
          <span className={`font-medium ${s.done ? "text-slate-700" : "text-slate-400"}`}>{s.label}</span>
          <span className={s.done ? "text-slate-600 tabular-nums" : "text-slate-300 italic"}>
            {s.done ? formatDateTime(s.at) : "In attesa"}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 leading-snug pt-1 border-t border-slate-100">
        Consegna e apertura dipendono dal client email della farmacia; non confermano il download degli allegati.
      </p>
    </div>
  );
}

export function StatoBadge({ stato, map }) {
  const cfg = map[stato] || { label: stato, color: "bg-slate-50 text-slate-600 border border-slate-200" };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

export function OspiteAvatar({ cognome, nome, className = "" }) {
  const initials = `${(cognome || nome || "?").slice(0, 1)}${(nome || cognome || "").slice(0, 1)}`.toUpperCase();
  return (
    <div className={`w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold shrink-0 ${className}`}>
      {initials}
    </div>
  );
}

export function ScadenzaCell({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  const isUrgent = diff <= 0;
  const label = diff === 0 ? "Oggi" : diff < 0 ? "Scaduta" : formatDate(date);
  return (
    <span className={isUrgent ? "text-red-600 font-semibold" : "text-slate-600"}>
      {isUrgent && diff === 0 ? "Scade oggi" : label}
    </span>
  );
}

export function FilterSelect({ children, className = "" }) {
  return (
    <select className={`rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 ${className}`}>
      {children}
    </select>
  );
}

export function FilterBar({ filters, search, onSearchChange, searchPlaceholder = "Cerca...", action }) {
  return (
    <div className="flex flex-col lg:flex-row gap-3 justify-between">
      <div className="flex flex-wrap gap-2 items-center">
        {filters}
        <Btn variant="secondary" className="px-3 py-2 text-slate-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
          </svg>
          Filtri
        </Btn>
      </div>
      <div className="flex gap-2">
        {search !== undefined && (
          <div className="relative flex-1 lg:flex-none">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
            </svg>
            <input
              className="w-full lg:w-64 rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
        )}
        {action}
      </div>
    </div>
  );
}

export function DetailPanel({ title, icon: Icon, onClose, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col ${className}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
          {Icon && <IconBubble icon={Icon} tone="violet" className="w-8 h-8" />}
          {title}
        </h3>
        {onClose && (
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      <div className="p-4 space-y-4 text-sm overflow-y-auto flex-1">{children}</div>
    </div>
  );
}

export const MAIN_TAB_TRIGGER =
  "flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none transition data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent";

export const SUB_TAB_TRIGGER =
  "relative flex-1 min-w-fit rounded-lg border border-transparent bg-transparent px-4 py-2.5 text-sm font-medium text-slate-600 shadow-none transition data-[state=active]:border-violet-300 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700";

export function Btn({ children, onClick, disabled, variant = "primary", className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-violet-600 text-white hover:bg-violet-700 shadow-sm shadow-violet-200",
    secondary: "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-sm",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200",
    success: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200",
    ghost: "text-slate-600 hover:bg-violet-50 hover:text-violet-700",
    soft: "bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-100",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

const TONE_STYLES = {
  violet: { icon: "text-violet-600", bg: "bg-violet-50", ring: "ring-violet-100" },
  amber: { icon: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100" },
  blue: { icon: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-100" },
  emerald: { icon: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" },
  red: { icon: "text-red-600", bg: "bg-red-50", ring: "ring-red-100" },
  cyan: { icon: "text-cyan-600", bg: "bg-cyan-50", ring: "ring-cyan-100" },
  slate: { icon: "text-slate-600", bg: "bg-slate-50", ring: "ring-slate-100" },
};

export function KpiCard({ icon: Icon, label, value, subtitle, color, tone = "violet" }) {
  const style = TONE_STYLES[tone] || TONE_STYLES.violet;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className={`w-10 h-10 rounded-xl ${style.bg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${color || style.icon}`} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-2xl font-bold leading-tight text-slate-900">{value ?? 0}</div>
          <div className="mt-0.5 text-sm font-medium text-slate-700 leading-snug">{label}</div>
          {subtitle && <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

export function SectionTitle({ title, action }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
  );
}

export function IconBubble({ icon: Icon, tone = "violet", className = "" }) {
  const style = TONE_STYLES[tone] || TONE_STYLES.violet;
  return (
    <div className={`w-9 h-9 rounded-2xl ${style.bg} ring-1 ${style.ring} flex items-center justify-center ${className}`}>
      <Icon className={`w-4 h-4 ${style.icon}`} />
    </div>
  );
}
